import { Grid, Theme, Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import {RouteComponentProps } from 'react-router-dom';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import {
	USER_STATUS_LOGIN_BAD_CREDENTIALS,
	USER_STATUS_LOGIN_FAILED,
	USER_STATUS_LOGIN_IN_PROGRESS,
	USER_STATUS_LOGIN_SUCCESS,
	USER_STATUS_PROFILE_LOADING,
} from 'redux/reducers/mobiversReducer';
import mobiversLogo from '../../assets/mobivers-logo-red.svg';
import { GenericInput } from '../../components/Form/GenericInput/GenericInput';
import { GenericButton } from '../../components/GenericButton/GenericButton';
import { LegalFooter } from '../../components/LegalFooter/LegalFooter';
import Loading from '../../components/Loading/Loading';
import { ROUTE_HOME } from '../../lib/constants';
import { useUniversalTracking } from '../../lib/tracking/universalTracking';
import { fetchContent as fetchContentAction } from '../../redux/actions/contentful';
import {getUrlDepandOnCustomization, updateCustomizing as updateCustomizingAction} from '../../redux/actions/customizing';
import { passwordLogin } from '../../redux/actions/mobivers';
import {
	CUSTOMIZING_STATUS_COMPLETED,
	CUSTOMIZING_STATUS_LOADING,
} from '../../redux/reducers/customizingReducer';
import { colors } from '../../theme/theme';
import { AppState, CustomizingState, UserState } from '../../types/redux';
import { getEnvironmentTitle } from '../../utils/environment';
import { onPressEnterHandlerFactory } from '../../utils/formInteraction';
import { useQueryParams } from '../../utils/router';
import { Link } from "react-router-dom"

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
	},
	mainSection: {
		flexDirection: 'column',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: theme.spacing(8),
	},
	footerSection: {},
	gridFormWrapper: {
		maxWidth: '310px',
	},
	gridItemHeader: {
		marginTop: '96px',
		textAlign: 'center',
	},
	gridItemFormField: {
		marginTop: '16px',
	},
	gridItemLoginButton: {
		marginTop: '24px',
	},
	gridItemFooter: {
		marginTop: '24px',
		textAlign: 'center',
	},
	logo: {
		maxWidth: '100%',
	},
	loginText: {
		color: '#4b4b4a',
		fontWeight: 500,
		fontSize: '22px',
		marginTop: theme.spacing(6),
		marginBottom: theme.spacing(1),
	},
	loginError: {
		color: colors.red,
		fontSize: '14px',
		lineHeight: '14px',
		marginBottom: '8px',
	},
	forgotPassword: {
		color: '#757575',
		fontSize: '14px',
		letterSpacing: '0.25px',
		lineHeight: '22px',
		marginTop: '52px',
		textDecoration: 'underline',
	},
	loginLoading: {
		position: 'absolute',
		top: 0,
		width: '100%',
	},
	envTitleWrapper: {
		width: '100%',
		textAlign: 'center',
		display: 'flex',
		justifyItems: 'center',
		alignItems: 'center',
		margin: theme.spacing(2, 0),
	},
	envTitleLine: {
		height: 2,
		backgroundColor: theme.palette.primary.main,
		flex: '1 1 25%',
	},
	envTitleText: {
		color: theme.palette.primary.main,
		margin: theme.spacing(0, 2),
	},
	loadingText: {
		color: colors.grayA80,
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
	},
	login_regist: {
		color: colors.white
	}
}));

export const LoginPreloader: React.FC<{ message: string }> = ({ message }) => {
	const classes = useStyles();

	return (
		<>
			<div className={classes.loginLoading}>
				<Loading showSpinner={true} />
			</div>
			<Grid item xs={10} className={classes.gridFormWrapper}>
				<Typography variant="h4" className={classes.loadingText}>
					{message}
				</Typography>
			</Grid>
		</>
	);
};

interface Props extends RouteComponentProps, StateProps, DispatchProps {}

const InternalLogin: React.FC<Props> = (props) => {
	const [userName, setUserName] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const { trackEvent, trackPageView } = useUniversalTracking();
	const userNameRef = React.useRef<HTMLElement>(null);
	const passwordRef = React.useRef<HTMLElement>(null);
	const formRefs = [userNameRef, passwordRef];
	const { fetchContent } = props;
	const { t } = useTranslation();
	const classes = useStyles();
	const environmentTitle = getEnvironmentTitle();
	const customizing: CustomizingState = useSelector<AppState, CustomizingState>(
		(state) => state.customizing
	);
	let isBank11 = customizing?.name==="bank11";
	const queryParams = useQueryParams();
	const [isLoadingGetParams, setIsLoadingGetParams] = useState<boolean>(true);
	// if query param customizingid given on page load, use it to fetch customizing
	useEffect(
		() => {
			if (queryParams?.customizingid ) {
				props.updateCustomizing(queryParams?.customizingid);
			} else {
				if(
					( /.*(-|^|\/\/)bank11(\.|-).*/.test(window.location.hostname) && (/\/|\/login/.test(window.location.pathname)) )
					// || /.*(-|^|\/\/)clpos.k8s.test(\.|-).*/.test( window.location.hostname )
					// || /.*localhost.*/.test( window.location.hostname ) //remove for build in jenkins
				) {
					const favicon = document.getElementById('favicon') as HTMLLinkElement | null;
					if (favicon != null) favicon.href = "/bank11/favicon.ico";
					props.updateCustomizing("Bank11");
				}
			}
			setIsLoadingGetParams(false);
		},
		// eslint-disable-next-line
		[]
	);

	// redirect to homepage after successful login
	useEffect(() => {
		if (
			props.user.status === USER_STATUS_LOGIN_SUCCESS &&
			props.customizing.status === CUSTOMIZING_STATUS_COMPLETED
		) {
			props.history.push(`/${ROUTE_HOME}`);
		}
	});

	// track pageview
	useEffect(
		() => {
			trackPageView(props.location.pathname);
		},
		// eslint-disable-next-line
		[props.location.pathname]
	);

	// fetch contentful content on startup
	useEffect(() => {
		fetchContent();
	}, [fetchContent]);

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const validateAndSubmit = () => props.logUser(userName, password);

	// event listener on input field key press events to focus next input or submit form
	const onKeyPressEnter = onPressEnterHandlerFactory(
		formRefs,
		validateAndSubmit
	);

	const handleClickLoginButton = () => {
		trackEvent({
			category: 'login',
			action: 'click-event',
			label: 'submit',
		});
		validateAndSubmit();
	};

	const isLoginFailed = Boolean(
		props.user.status === USER_STATUS_LOGIN_FAILED ||
			props.user.status === USER_STATUS_LOGIN_BAD_CREDENTIALS
	);

	// don't show form while loading customization or logging in with user credentials
	const isLoading =
		isLoadingGetParams ||
		props.user.status === USER_STATUS_LOGIN_IN_PROGRESS ||
		props.user.profileStatus === USER_STATUS_PROFILE_LOADING ||
		props.customizing.status === CUSTOMIZING_STATUS_LOADING;

	return (
		<div className={classes.root} data-cy={'LoginPage'}>
			<Grid container justifyContent="center" className={classes.mainSection}>
				{isLoading && <LoginPreloader message="Sie werden angemeldet ..." />}
				{!isLoading && (
					<Grid item xs={10} className={classes.gridFormWrapper}>
						<Grid item xs className={classes.gridItemHeader}>
							<img
								className={classes.logo}
								src={customizing.logoSrc ?? mobiversLogo}
								alt="mobivers logo"
								style={{ width: '90%' }}
							/>
						</Grid>

						<Grid item xs style={{ textAlign: 'center' }}>
							{environmentTitle && (
								<div className={classes.envTitleWrapper}>
									<div className={classes.envTitleLine} />
									<Typography variant="h3" className={classes.envTitleText}>
										{getEnvironmentTitle()}
									</Typography>
									<div className={classes.envTitleLine} />
								</div>
							)}
							<div className={classes.loginText}>{t('login.text')}</div>
						</Grid>
						<Grid item xs={12} className={classes.gridItemFormField}>
							<GenericInput
								inputId="login_user"
								inputValue={userName}
								setInputValue={setUserName}
								label={t('login.e-mail')}
								additionalInputParams={{
									inputRef: userNameRef,
									onKeyPress: onKeyPressEnter,
								}}
							/>
						</Grid>
						<Grid item xs={12} className={classes.gridItemFormField}>
							<GenericInput
								inputId="login_password"
								inputValue={password}
								setInputValue={setPassword}
								type={showPassword ? 'text' : 'password'}
								label={t('login.password')}
								endAdornement={
									<IconButton
										aria-label="toggle password visibility"
										onClick={handleClickShowPassword}
									>
										{showPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								}
								additionalInputParams={{
									inputRef: passwordRef,
									onKeyPress: onKeyPressEnter,
								}}
							/>
						</Grid>
						<Grid item xs={12} className={classes.gridItemLoginButton}>
							<GenericButton
								onClick={handleClickLoginButton}
								data-cy="loginButton"
								fullWidth={true}
							>
								{t('login.submit')}
							</GenericButton>
						</Grid>

						{isBank11 && <Grid item xs={12} className={classes.gridItemLoginButton}>
							<Link
									className={classes.login_regist}
									id="login_regist"
									target="_blank"
									to={{pathname: 'https://newsletter.creditlife.net/bank11-handler-registrierung'}}
								>
									<GenericButton
										data-cy="extRegistrationButton"
										fullWidth={true}
										// className={`${classes.button} ${{color: inherit}}`}
									>
											{t('login.regist')}
									</GenericButton>
							</Link>
						</Grid> }
						{/*<Grid item xs={12} className={classes.gridItemFooter}>*/}
						{/*	<GenericButton*/}
						{/*		onClick={handleClickRegistration}*/}
						{/*		data-cy="loginButton"*/}
						{/*		fullWidth={true}*/}
						{/*	>*/}
						{/*		{t('login.regist')}*/}
						{/*	</GenericButton>*/}
						{/*</Grid>*/}
						<Grid item xs={12} className={classes.gridItemFooter}>
							{isLoginFailed && (
								<div className={classes.loginError} id="login_error">
									{t('login.error')}
								</div>
							)}
							<Link className={classes.forgotPassword}
								  id="login_forgot_password"
								  target="_blank"
								  to={{pathname: getUrlDepandOnCustomization(customizing, process.env.REACT_APP_LOGIN_RESET_URL)}} >
								{t('login.forgot_password')}
							</Link>
						</Grid>
					</Grid>
				)}
			</Grid>
			<LegalFooter />
		</div>
	);
};

interface StateProps {
	user: UserState;
	customizing: CustomizingState;
}

interface DispatchProps {
	logUser(name: string, password: string): void;
	fetchContent(): void;
	updateCustomizing(id?: string): void;
}

const mapStateToProps = (state: AppState) => ({
	user: state.user,
	customizing: state.customizing,
});

/* istanbul ignore next */
const mapDispatchToProps = (
	dispatch: ThunkDispatch<Promise<any>, void, Action>
) => ({
	fetchContent: () => {
		dispatch(fetchContentAction());
	},
	logUser: (email: string, password: string) => {
		dispatch(passwordLogin(email, password));
	},
	updateCustomizing: (id?: string) => {
		dispatch(updateCustomizingAction(id));
	},
});

export const Login = connect(
	mapStateToProps,
	mapDispatchToProps
)(InternalLogin);
